import { computed, inject, Injectable, signal } from '@angular/core';
import { partition } from 'lodash-es';
import { SELECTED_LANGUAGE } from 'src/app/@constants/local-storage-key-constants';
import { Language } from 'src/app/@models/language/language.model';
import { EventConfigStateService } from 'src/app/@store/event-config-state.service';
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from 'src/app/@utils/local-storage-util';

interface LanguageState {
  availableLanguages: Language[];
  originalLanguageCode: string;
  selectedLanguage: Language;
}

const initialState: LanguageState = {
  availableLanguages: [],
  originalLanguageCode: 'en',
  selectedLanguage: {
    code: 'en',
    value: 'English',
    label: 'EN - English',
  },
};

const state = {
  availableLanguages: signal<Language[]>(initialState.availableLanguages),
  selectedLanguage: signal<Language>(initialState.selectedLanguage),
  originalLanguageCode: signal<string>(initialState.originalLanguageCode),
};

@Injectable({
  providedIn: 'root',
})
export class LanguageStore {
  public readonly $originalLanguageCode =
    state.originalLanguageCode.asReadonly();
  public readonly $selectedLanguage = state.selectedLanguage.asReadonly();
  public readonly $availableLanguages = computed(() => {
    const languages = state.availableLanguages();
    const originalCode = state.originalLanguageCode();
    const [originalLanguages, otherLanguages] = partition(
      languages,
      (lang) => lang.code === originalCode
    );
    return [...originalLanguages, ...otherLanguages];
  });

  private readonly _eventConfigStore = inject(EventConfigStateService);

  setAvailableLanguages(languages: Language[]): void {
    state.availableLanguages.set(languages);
  }

  setOriginalLanguageCode(languageCode: string): void {
    state.originalLanguageCode.set(languageCode);
    this._initializeSelectedLanguage();
  }

  setSelectedLanguage(language: Language): void {
    state.selectedLanguage.set(language);
    setLocalStorageItem(SELECTED_LANGUAGE, language);
  }

  private _initializeSelectedLanguage(): void {
    const isPrimaryScreen = this._eventConfigStore.$isPrimaryScreen();
    const selectedLanguageFromStorage =
      getLocalStorageItem<Language>(SELECTED_LANGUAGE);

    if (selectedLanguageFromStorage && !isPrimaryScreen) {
      state.selectedLanguage.set(selectedLanguageFromStorage);
      return;
    }

    const originalLanguageCode = state.originalLanguageCode();
    const language =
      state
        .availableLanguages()
        ?.find((language) => language.code === originalLanguageCode) ??
      initialState.selectedLanguage;
    state.selectedLanguage.set(language);
    setLocalStorageItem(SELECTED_LANGUAGE, language);
  }
}
